/**
 * Required Uploads Agent: supplemental documents a listing needs before it can
 * be published, e.g. the interim Wisconsin § 709.02(2) condominium addendum
 * (registry `required_uploads`, code `wi_condo_recr_addendum`).
 *
 * Contract (backend/app/routes/houses.py, backend/app/services/paperwork/uploads.py):
 *   POST /api/houses/{houseId}/required-uploads/{code}
 *     multipart/form-data: `file`, and the seller's attestation, REQUIRED for
 *     any upload type with attestation copy (legal L4 §9.3):
 *       `certify` ('true'), `signature_name` (typed full name),
 *       `attestation_version` (the current one)
 *     → 201 {code, uploaded, original_filename, uploaded_at, id, title,
 *            file_size_bytes, attested, attestation_version, paperwork_category}
 *     422 {code: 'paperwork_upload_incomplete', fields: ['certify'|'signature_name'], attestation_version}
 *     409 {code: 'paperwork_upload_attestation_stale', attestation_version}
 *   There is no separate "attest" call: an uploaded-but-unattested file is
 *   re-attested by uploading it again with the attestation.
 *
 *   The house GET exposes the state as `paperwork.required_uploads`:
 *     [{code, title, uploaded, original_filename, uploaded_at, attested,
 *       attestation_required, attestation_version, attestation_text}]
 *   (GET /api/houses/{id}/required-uploads returns the same per-type fields).
 *   Publishing without an uploaded AND attested file is refused with
 *     409 {code: 'required_upload_missing', upload_code, unattested_uploads, message}.
 */
import APIAgent from './apiAgent';

/** Matches the contingency and condo-document uploads (25 MB). */
export const MAX_REQUIRED_UPLOAD_BYTES = 25 * 1024 * 1024;
export const REQUIRED_UPLOAD_ACCEPT = '.pdf,application/pdf,image/jpeg,image/png';

/** Error codes the upload endpoint answers with (uploads.py). */
export const UPLOAD_INCOMPLETE_CODE = 'paperwork_upload_incomplete';
export const UPLOAD_ATTESTATION_STALE_CODE = 'paperwork_upload_attestation_stale';

export const RequiredUploadsAPI = {
  /**
   * Upload (or replace) one required document, with the seller's attestation.
   *
   * @param {number} houseId
   * @param {string} code - e.g. 'wi_condo_recr_addendum'
   * @param {File} file
   * @param {{signatureName: string, certify: boolean, attestationVersion: string}} [attestation]
   * @returns {Promise<{code: string, uploaded: boolean, attested?: boolean,
   *   original_filename?: string, uploaded_at?: string}>}
   */
  upload: (houseId, code, file, attestation) => {
    const body = new FormData();
    body.append('file', file);
    if (attestation) {
      body.append('certify', attestation.certify ? 'true' : 'false');
      body.append('signature_name', attestation.signatureName || '');
      body.append('attestation_version', attestation.attestationVersion || '');
    }
    return APIAgent.request(
      `/api/houses/${houseId}/required-uploads/${encodeURIComponent(code)}`,
      'POST',
      body,
      {},
      { timeout: 60000 },
    );
  },
};

/**
 * The server's upload state for a house, keyed by code, from
 * `house.paperwork.required_uploads` (entries may be objects or bare codes of
 * uploads that are still outstanding).
 *
 * `attestationRequired` defaults to true when the server doesn't say: the
 * only upload type today (the WI condo addendum) requires it, and assuming
 * so fails closed (the seller is asked to sign rather than let through).
 */
export function uploadsFromPaperwork(paperwork) {
  const list = Array.isArray(paperwork?.required_uploads) ? paperwork.required_uploads : [];
  return list.reduce((acc, entry) => {
    if (entry && typeof entry === 'object' && entry.code) {
      acc[entry.code] = {
        uploaded: entry.uploaded === true,
        attested: entry.attested === true,
        attestationRequired: entry.attestation_required !== false,
        originalFilename: entry.original_filename || null,
        uploadedAt: entry.uploaded_at || null,
        ...(entry.attestation_text ? { attestationText: entry.attestation_text } : {}),
        ...(entry.attestation_version ? { attestationVersion: entry.attestation_version } : {}),
      };
    }
    return acc;
  }, {});
}

/**
 * True when an upload satisfies the publish gate: a live file, attested in the
 * current version whenever attestation is required (legal L4 §9.3, gate G4).
 */
export function isUploadComplete(upload) {
  if (!upload?.uploaded) return false;
  return upload.attestationRequired === false || upload.attested === true;
}

export default RequiredUploadsAPI;
